import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import { checkboxMultiProps } from "./utils/MultiSelectCheckbox";
import { dnAlert } from "./utils/dNews_Alert";
import { FaPaperclip, FaFilePdf, FaFileExcel, FaTimes } from "react-icons/fa";
import { apiFetch, API_BASE } from "./utils/api";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { addEagleWatermark } from "./utils/pdfWatermark";
import ExcelJS from "exceljs";
import logo_eagle from "./assets/img/logo_eagle.png";
import DOLLUZ_CORP from "./assets/img/DOLLUZ_CORP.png";
import "./dSpr.css";
import "./Leave_approvals.css";

const Leave_approvals = ({ navSize }) => {
    const [leaveRequests, setLeaveRequests] = useState([]);
    const [filteredRequests, setFilteredRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [rejectReason, setRejectReason] = useState("");
    const [selectedLeaveId, setSelectedLeaveId] = useState(null);
    const [selectedFilter, setSelectedFilter] = useState("leave_status");
    const [filters, setFilters] = useState({ leave_status: [] });
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedIds, setSelectedIds] = useState([]);
    const [isBulkReject, setIsBulkReject] = useState(false);
    const rowsPerPage = 10;
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    // Approve review panel - the paid/unpaid decision is made here.
    const [showApproveModal, setShowApproveModal] = useState(false);
    const [approveLoading, setApproveLoading] = useState(false);
    const [approveCtx, setApproveCtx] = useState(null);
    const [payChoice, setPayChoice] = useState("Auto");
    const navigate = useNavigate();

    useEffect(() => {
        fetchLeaveRequests();
        fetchloginedEmployees();
    }, []);

    const fetchloginedEmployees = async () => {
        try {
            const res = await apiFetch(`/api/employee/logined_employee`, {
                method: 'GET',
                credentials: 'include',
            });
            const data = await res.json();

            if (data?.message === "Access Denied. No Token Provided!" ||
                data?.message === "Invalid Token") {
                navigate("/login");
                return;
            }

            // Role redirect removed - ProtectedRoute (App.js) gates via matrix.

        } catch (err) {
            console.error("Error fetching employees:", err);
        }
    };

    // ✅ Fetch leave requests
    const fetchLeaveRequests = async () => {
        try {
            const res = await apiFetch(`/api/leave/leave_requests_list`);
            const data = await res.json();
            // Backend errors return { error: "..." } not an array - guard so
            // downstream .filter / .slice calls don't crash the whole page.
            const list = Array.isArray(data) ? data : [];
            setLeaveRequests(list);
            setFilteredRequests(list);
        } catch (err) {
            console.error("❌ Error fetching leave requests:", err);
        } finally {
            setLoading(false);
        }
    };

    // 🔹 Filter Options
    const filterOptions = [
        { value: "leave_status", label: "Status" },
        { value: "leave_type", label: "Leave type" },
        { value: "emp_name", label: "Employee" },
        { value: "created_time", label: "Created date" },
    ];

    const getOptions = (key) => {
        const values = [...new Set(leaveRequests.map((req) => req[key] || "-"))];
        return values.map((v) => ({ value: v, label: v }));
    };

    // 🔹 Filtering Logic
    useEffect(() => {
        const filterableColumns = Object.keys(filters);

        const filtered = leaveRequests.filter((req) =>
            filterableColumns.every((col) => {
                // 🔵 Date Range Filter
                if (col === "created_time") {
                    const reqDate = new Date(req.created_time);

                    if (fromDate && !toDate)
                        return reqDate >= new Date(fromDate);

                    if (!fromDate && toDate)
                        return reqDate <= new Date(toDate);

                    if (fromDate && toDate)
                        return (
                            reqDate >= new Date(fromDate) &&
                            reqDate <= new Date(toDate)
                        );

                    return true;
                }

                // 🔴 Multi-select filters
                if (!filters[col] || filters[col].length === 0) return true;

                return filters[col].some((f) => f.value === req[col]);
            })
        );

        setFilteredRequests(filtered);
        setCurrentPage(1);
    }, [filters, leaveRequests, fromDate, toDate]);

    // 🔹 Pagination
    const totalPages = Math.ceil(filteredRequests.length / rowsPerPage);
    const paginatedRequests = filteredRequests.slice(
        (currentPage - 1) * rowsPerPage,
        currentPage * rowsPerPage
    );

    // ✅ Approve - opens the review panel (paid/unpaid is decided here), not a
    // bare yes/no, because approving is also where the pay call gets made.
    const handleApprove = async (id) => {
        setApproveCtx(null); setPayChoice("Auto");
        setShowApproveModal(true); setApproveLoading(true);
        try {
            const res = await apiFetch(`/api/leave/approval_context/${id}`, { credentials: "include" });
            const data = await res.json();
            if (!res.ok || !data.success) {
                setShowApproveModal(false);
                dnAlert.error("Couldn't load request", data.message || "Please try again.");
                return;
            }
            setApproveCtx(data);
        } catch (e) {
            setShowApproveModal(false);
            dnAlert.error("Couldn't load request", "Please try again.");
        } finally { setApproveLoading(false); }
    };

    const confirmApprove = () => {
        if (!approveCtx) return;
        setShowApproveModal(false);
        handleStatusUpdate(approveCtx.request.leave_requests_id, "Approved", null, payChoice);
    };

    // ✅ Reject Modal Handling
    const handleReject = (id) => {
        setSelectedLeaveId(id);
        setRejectReason("");
        setShowRejectModal(true);
    };

    const confirmReject = async () => {
        if (!rejectReason.trim()) {
            dnAlert.warning("Please enter a reason before rejecting.", "");
            return;
        }

        if (isBulkReject) {
            // ✅ Bulk reject mode
            for (const id of selectedIds) {
                await handleStatusUpdate(id, "Rejected", rejectReason);
            }
            dnAlert.success("Selected leaves rejected successfully.", "");
            setSelectedIds([]);
        } else {
            // ✅ Single reject mode
            handleStatusUpdate(selectedLeaveId, "Rejected", rejectReason);
        }

        setShowRejectModal(false);
        setIsBulkReject(false);
    };

    // ✅ Common Update Function
    // payOverride: "Auto" (default, and what bulk actions always use) | "Paid" | "Unpaid"
    const handleStatusUpdate = async (id, status, reason = null, payOverride = "Auto") => {
        try {
            const res = await apiFetch(`/api/leave/update_status/${id}`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ status, reason, pay_override: payOverride }),
                credentials: 'include',
            });

            if (res.ok) {
                dnAlert.success(`Leave ${status.toLowerCase()}`, "");
                fetchLeaveRequests();
            } else {
                dnAlert.error("Failed to update status.", "");
            }
        } catch (err) {
            console.error("Error updating leave status:", err);
        }
    };

    const handleBulkAction = async (status) => {
        if (selectedIds.length === 0) {
            dnAlert.warning("No rows selected", "Tick the checkboxes of the requests you want to act on.");
            return;
        }

        if (status === "Rejected") {
            setIsBulkReject(true);
            setShowRejectModal(true);
            return; // ❌ Don't proceed here - handled by modal
        }

        const ok = await dnAlert.confirm({
            title: `${status} selected leaves?`,
            message: `${selectedIds.length} request${selectedIds.length === 1 ? "" : "s"} will be ${status.toLowerCase()}.`,
            confirmLabel: status,
            danger: status === "Rejected",
        });
        if (!ok) return;
        for (const id of selectedIds) {
            await handleStatusUpdate(id, status);
        }
        dnAlert.success(`Selected leaves ${status.toLowerCase()}`, "");
        setSelectedIds([]);
    };

    // ✅ Export to PDF Function (SweetAlert + Only Selected Rows)
    const exportToPDF = (leaveRequests) => {
        let exportData =
            selectedIds.length > 0
                ? leaveRequests.filter((r) => selectedIds.includes(r.leave_requests_id))
                : [];

        if (exportData.length === 0) {
            dnAlert.warning("No rows selected!", "Please select at least one leave request to export.");
            return;
        }

        const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a3" });
        const pageWidth = doc.internal.pageSize.getWidth();

        // ── dSpr-style theme ───────────────────────────────────────────
        const NAVY       = [13, 27, 42];
        const ORANGE     = [232, 82, 10];
        const HEADER_SUB = [170, 187, 204];
        const SLATE      = [100, 116, 139];
        const ZEBRA      = [248, 250, 252];
        const LINE       = [226, 232, 240];

        // Navy header band
        doc.setFillColor(NAVY[0], NAVY[1], NAVY[2]);
        doc.rect(0, 0, pageWidth, 60, "F");
        doc.setFont("helvetica", "bold");
        doc.setFontSize(18);
        doc.setTextColor(255, 255, 255);
        doc.text("Dolluz Corp", 28, 32);
        const wDol = doc.getTextWidth("Dolluz Corp");
        doc.setTextColor(ORANGE[0], ORANGE[1], ORANGE[2]);
        doc.text(".", 28 + wDol, 32);
        const wDot = doc.getTextWidth(".");
        doc.text("    dTime", 28 + wDol + wDot, 32);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        doc.setTextColor(HEADER_SUB[0], HEADER_SUB[1], HEADER_SUB[2]);
        doc.text("Leave Requests Report - Generated by dAdmin", 28, 48);

        // Title + meta
        doc.setFont("helvetica", "bold");
        doc.setFontSize(13);
        doc.setTextColor(NAVY[0], NAVY[1], NAVY[2]);
        doc.text("Leave Requests Report", 28, 84);

        doc.setFont("helvetica", "normal");
        doc.setFontSize(9.5);
        doc.setTextColor(SLATE[0], SLATE[1], SLATE[2]);
        const generated = new Date().toLocaleString("en-IN", { timeZone: "Asia/Kolkata" });
        doc.text(
            `Generated: ${generated}    ·    ${exportData.length} row${exportData.length === 1 ? "" : "s"}`,
            28, 100
        );

        autoTable(doc, {
            startY: 116,
            margin: { left: 28, right: 28 },
            head: [[
                "Request ID", "Employee", "Leave Type",
                "Start Date (Breakdown)", "End Date (Breakdown)",
                "Days", "Description", "Attachment", "Status"
            ]],
            body: exportData.map((leave) => {
                const start = new Date(leave.start_date);
                const end = new Date(leave.end_date);
                let diffDays = (end - start) / (1000 * 60 * 60 * 24) + 1;
                if (leave.start_date_breakdown !== "Full") diffDays -= 0.5;
                if (leave.end_date_breakdown !== "Full") diffDays -= 0.5;
                return [
                    leave.leave_requests_id,
                    leave.emp_name || leave.emp_id,
                    leave.leave_type || "-",
                    `${new Date(leave.start_date).toLocaleDateString()} (${leave.start_date_breakdown})`,
                    `${new Date(leave.end_date).toLocaleDateString()} (${leave.end_date_breakdown})`,
                    diffDays.toFixed(1),
                    leave.leave_description || "-",
                    leave.attachment ? "Yes" : "No",
                    leave.leave_status || "Pending",
                ];
            }),
            theme: "plain",
            styles: { font: "helvetica", fontSize: 8.5, cellPadding: 5, textColor: NAVY, lineColor: LINE, lineWidth: 0.4 },
            headStyles: { fillColor: NAVY, textColor: 255, fontStyle: "bold", fontSize: 9, halign: "left" },
            alternateRowStyles: { fillColor: ZEBRA },
            columnStyles: { 6: { cellWidth: 100 } },
            didDrawPage: (hookData) => {
                const ph = doc.internal.pageSize.getHeight();
                doc.setFontSize(8);
                doc.setTextColor(SLATE[0], SLATE[1], SLATE[2]);
                doc.text(`Dolluz Corp · dTime · Page ${hookData.pageNumber}`, 28, ph - 18);
            },
        });

        addEagleWatermark(doc);
        doc.save("Leave_Requests_Report.pdf");
        dnAlert.success("Exported", "Selected leave requests exported as PDF.");
    };

    // ✅ Export to Excel Function (SweetAlert + Only Selected Rows)
    const exportToExcel = async (leaveRequests) => {
        let exportData =
            selectedIds.length > 0
                ? leaveRequests.filter((r) => selectedIds.includes(r.leave_requests_id))
                : [];

        if (exportData.length === 0) {
            dnAlert.warning("No rows selected!", "Please select at least one leave request to export.");
            return;
        }

        const workbook = new ExcelJS.Workbook();
        workbook.creator = "Dolluz Corp · dTime";
        workbook.created = new Date();
        const worksheet = workbook.addWorksheet("Leave Requests");

        // ── dSpr-style palette ─────────────────────────────────────────
        const XL_NAVY   = "FF0D1B2A";
        const XL_ORANGE = "FFE8520A";
        const XL_SUB    = "FFAABBCC";
        const XL_SLATE  = "FF64748B";
        const XL_LINE   = "FFE2E8F0";
        const XL_ZEBRA  = "FFFAFBFD";

        const headers = [
            "Request ID", "Employee", "Leave Type",
            "Start Date (Breakdown)", "End Date (Breakdown)",
            "Requested Days", "Description", "Attachment", "Status",
        ];
        const cols = headers.length;

        // Pre-set column widths
        worksheet.columns = [16, 24, 18, 22, 22, 14, 38, 22, 14].map(w => ({ width: w }));

        // ── Navy header band: "Dolluz Corp." + orange period + dTime ───
        const brandRow = worksheet.addRow([]);
        worksheet.mergeCells(brandRow.number, 1, brandRow.number, cols);
        const brandCell = brandRow.getCell(1);
        brandCell.value = {
            richText: [
                { text: "Dolluz Corp",  font: { bold: true, color: { argb: "FFFFFFFF" }, size: 16, name: "Calibri" } },
                { text: ".",            font: { bold: true, color: { argb: XL_ORANGE }, size: 16, name: "Calibri" } },
                { text: "    dTime",    font: { bold: true, color: { argb: XL_ORANGE }, size: 16, name: "Calibri" } },
            ],
        };
        brandCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: XL_NAVY } };
        brandCell.alignment = { vertical: "middle", indent: 1 };
        brandRow.height = 28;

        const subRow = worksheet.addRow(["Leave Requests Report - Generated by dAdmin"]);
        worksheet.mergeCells(subRow.number, 1, subRow.number, cols);
        const subCell = subRow.getCell(1);
        subCell.font = { color: { argb: XL_SUB }, size: 10, name: "Calibri" };
        subCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: XL_NAVY } };
        subCell.alignment = { vertical: "middle", indent: 1 };
        subRow.height = 18;

        worksheet.addRow([]);

        // ── Meta block ─────────────────────────────────────────────────
        const generated = new Date().toLocaleString("en-IN", { timeZone: "Asia/Kolkata" }) + " IST";
        [["Generated:", generated], ["Rows:", String(exportData.length)]].forEach(([k, v]) => {
            const r = worksheet.addRow([k, v]);
            r.getCell(1).font = { color: { argb: XL_SLATE }, size: 10 };
            r.getCell(2).font = { bold: true, color: { argb: XL_NAVY }, size: 10 };
        });
        worksheet.addRow([]);

        // ── Table header ───────────────────────────────────────────────
        const headerRow = worksheet.addRow(headers);
        headerRow.eachCell((cell) => {
            cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: XL_NAVY } };
            cell.font = { bold: true, color: { argb: "FFFFFFFF" }, size: 10.5, name: "Calibri" };
            cell.alignment = { horizontal: "left", vertical: "middle", indent: 1 };
            cell.border = { bottom: { style: "thin", color: { argb: XL_LINE } } };
        });
        headerRow.height = 22;

        exportData.forEach((leave, idx) => {
            const start = new Date(leave.start_date);
            const end = new Date(leave.end_date);
            let diffDays = (end - start) / (1000 * 60 * 60 * 24) + 1;
            if (leave.start_date_breakdown !== "Full") diffDays -= 0.5;
            if (leave.end_date_breakdown !== "Full") diffDays -= 0.5;

            const row = [
                leave.leave_requests_id,
                leave.emp_name || leave.emp_id,
                leave.leave_type || "-",
                `${new Date(leave.start_date).toLocaleDateString()} (${leave.start_date_breakdown})`,
                `${new Date(leave.end_date).toLocaleDateString()} (${leave.end_date_breakdown})`,
                diffDays.toFixed(1),
                leave.leave_description || "-",
                leave.attachment ? leave.attachment.split("/").pop() : "No File",
                leave.leave_status || "Pending",
            ];

            const dataRow = worksheet.addRow(row);
            dataRow.eachCell((cell, col) => {
                cell.alignment = { vertical: "top", wrapText: col === 7 };
                cell.font = { color: { argb: XL_NAVY }, size: 10, name: "Calibri" };
                if (idx % 2 === 1) cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: XL_ZEBRA } };
                cell.border = { bottom: { style: "hair", color: { argb: XL_LINE } } };
            });
            // Status accent in the last column
            const s = String(leave.leave_status || "").toLowerCase();
            const statusCell = dataRow.getCell(9);
            if (s === "approved")      statusCell.font = { bold: true, color: { argb: "FF15803D" }, size: 10 };
            else if (s === "rejected") statusCell.font = { bold: true, color: { argb: "FFB91C1C" }, size: 10 };
            else if (s === "pending")  statusCell.font = { bold: true, color: { argb: XL_ORANGE }, size: 10 };
        });

        worksheet.columns.forEach((col) => {
            let maxLength = 10;
            col.eachCell({ includeEmpty: true }, (cell) => {
                const len = cell.value ? cell.value.toString().length : 10;
                maxLength = Math.max(maxLength, len + 2);
            });
            col.width = Math.min(maxLength, 40);
        });

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `Leave_Requests_Report.xlsx`;
        link.click();

        dnAlert.success("Exported!", "Selected leave requests exported as Excel.");
    };

    return (
        <div className="leave-requests-container">
            <div className={`leave-requests-dashboard ${navSize}`}>
                <div className="dspr-header-wrapper">
                    <h2>
                        <a className="navbar-brand" href="#">
                            <img
                                src={logo_eagle}
                                alt="dAdmin Logo"
                                style={{ height: "50px", objectFit: "contain", marginLeft: "-20px" }}
                            />
                        </a>
                        Leave Approvals
                    </h2>
                    <p className="dspr-subtitle">Review and act on pending leave requests. Filter, search, bulk approve or reject, export to PDF / Excel.</p>
                </div>

                <div className="leave-requests-content">
                    <div className="leave-filters-container">
                        <div className="filters-left">
                            <Select
                                options={filterOptions}
                                value={filterOptions.find((f) => f.value === selectedFilter)}
                                onChange={(val) => {
                                    setSelectedFilter(val.value);
                                    setFilters((prev) => ({ ...prev, [val.value]: [] }));
                                    setCurrentPage(1);
                                }}
                                placeholder="Select filter"
                                isSearchable
                                styles={{ container: (provided) => ({ ...provided, width: 220 }) }}
                            />
                            {selectedFilter === "created_time" ? (
                                <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>

                                    <input
                                        type="date"
                                        value={fromDate}
                                        onClick={(e) => e.target.showPicker?.()}
                                        onChange={(e) => {
                                            setFromDate(e.target.value);
                                            setCurrentPage(1);
                                        }}
                                        className="date-input"
                                    />

                                    <span style={{ fontWeight: "bold" }}>to</span>

                                    <input
                                        type="date"
                                        value={toDate}
                                        onClick={(e) => e.target.showPicker?.()}
                                        onChange={(e) => {
                                            setToDate(e.target.value);
                                            setCurrentPage(1);
                                        }}
                                        className="date-input"
                                    />

                                    {(fromDate || toDate) && (
                                        <FaTimes
                                            size={18}
                                            style={{ cursor: "pointer", color: "#555555" }}
                                            title="Clear Date Range"
                                            onClick={() => {
                                                setFromDate("");
                                                setToDate("");
                                                setCurrentPage(1);
                                            }}
                                        />
                                    )}
                                </div>
                            ) : (
                                <Select
                                    placeholder={`Filter by ${filterOptions.find((f) => f.value === selectedFilter).label
                                        }`}
                                    options={getOptions(selectedFilter)}
                                    value={filters[selectedFilter]}
                                    onChange={(vals) =>
                                        setFilters((prev) => ({
                                            ...prev,
                                            [selectedFilter]: vals || [],
                                        }))
                                    }
                                    isMulti
                                    {...checkboxMultiProps}
                                    isClearable
                                    styles={{
                                        container: (p) => ({ ...p, width: 250 }),
                                        multiValue: (p) => ({ ...p, backgroundColor: "#e0e0e0" }),
                                    }}
                                />
                            )}

                            <div className="bulk-actions">
                                <button
                                    className="approve-btn"
                                    onClick={() => handleBulkAction("Approved")}
                                >
                                    Bulk Approve
                                </button>

                                <button
                                    className="reject-btn"
                                    onClick={() => {
                                        if (selectedIds.length === 0) {
                                            dnAlert.warning("No rows selected", "Tick the checkboxes of the requests you want to reject.");
                                            return;
                                        }
                                        setIsBulkReject(true);
                                        setShowRejectModal(true);
                                        setRejectReason("");
                                    }}
                                >
                                    Bulk Reject
                                </button>

                                {/* Export icons */}
                                <FaFilePdf
                                    size={24}
                                    color="#e74c3c"
                                    title="Export as PDF"
                                    onClick={() => exportToPDF(filteredRequests)}
                                />
                                <FaFileExcel
                                    size={24}
                                    color="#27ae60"
                                    title="Export as Excel"
                                    onClick={() => exportToExcel(filteredRequests)}
                                />
                            </div>
                        </div>
                    </div>

                    {loading ? (
                        <p className="loading-text">Loading leave requests...</p>
                    ) : (
                        <div className="table-wrapper">
                            <table className="leave-table">
                                <thead>
                                    <tr>
                                        <th>
                                            <input
                                                type="checkbox"
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        setSelectedIds(paginatedRequests.map((r) => r.leave_requests_id));
                                                    } else {
                                                        setSelectedIds([]);
                                                    }
                                                }}
                                                checked={
                                                    paginatedRequests.length > 0 &&
                                                    selectedIds.length === paginatedRequests.length
                                                }
                                            />
                                        </th>
                                        <th>ID</th>
                                        <th>Employee</th>
                                        <th>Leave Type</th>
                                        <th>Start Date</th>
                                        <th>End Date</th>
                                        <th>Requested Days</th>
                                        <th>Description</th>
                                        <th>Attachment</th>
                                        <th>Status</th>
                                        <th>Approval</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginatedRequests.length > 0 ? (
                                        paginatedRequests.map((leave) => {
                                            const start = new Date(leave.start_date);
                                            const end = new Date(leave.end_date);
                                            let diffDays = (end - start) / (1000 * 60 * 60 * 24) + 1;
                                            if (leave.start_date_breakdown !== "Full") diffDays -= 0.5;
                                            if (leave.end_date_breakdown !== "Full") diffDays -= 0.5;

                                            return (
                                                <tr key={leave.leave_requests_id}>
                                                    {/* ✅ Selection Checkbox */}
                                                    <td style={{ textAlign: "center" }}>
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedIds.includes(leave.leave_requests_id)}
                                                            onChange={(e) => {
                                                                if (e.target.checked) {
                                                                    setSelectedIds((prev) => [...prev, leave.leave_requests_id]);
                                                                } else {
                                                                    setSelectedIds((prev) =>
                                                                        prev.filter((id) => id !== leave.leave_requests_id)
                                                                    );
                                                                }
                                                            }}
                                                        />
                                                    </td>

                                                    <td>{leave.leave_requests_id}</td>
                                                    <td>{leave.emp_name || leave.emp_id}</td>
                                                    <td>{leave.leave_type || "-"}</td>
                                                    <td>
                                                        {new Date(leave.start_date).toLocaleDateString()} (
                                                        {leave.start_date_breakdown})
                                                    </td>
                                                    <td>
                                                        {new Date(leave.end_date).toLocaleDateString()} (
                                                        {leave.end_date_breakdown})
                                                    </td>
                                                    <td style={{ textAlign: "center" }}>{diffDays.toFixed(1)}</td>

                                                    <td
                                                        style={{
                                                            maxWidth: "200px",
                                                            whiteSpace: "nowrap",
                                                            overflow: "hidden",
                                                            textOverflow: "ellipsis",
                                                        }}
                                                        title={leave.leave_description}
                                                    >
                                                        {leave.leave_description || "-"}
                                                    </td>

                                                    <td style={{ textAlign: "center" }}>
                                                        {leave.attachment ? (() => {
                                                            // Show the ORIGINAL filename (part after "__") as the
                                                            // tooltip so admins see the meaningful name, not the raw
                                                            // "<timestamp>__<name>" stored path.
                                                            const base = leave.attachment.replace(/\\/g, "/").split("/").pop();
                                                            const idx = base.indexOf("__");
                                                            const displayName = idx >= 0 ? base.slice(idx + 2) : base;
                                                            return (
                                                                <a
                                                                    href={`${API_BASE}/api/leave/attachment/${leave.leave_requests_id}`}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    title={displayName}
                                                                    style={{ color: "#4a90e2", fontSize: "18px" }}
                                                                >
                                                                    <FaPaperclip />
                                                                </a>
                                                            );
                                                        })() : (
                                                            <span style={{ color: "#999" }}>No File</span>
                                                        )}
                                                    </td>

                                                    <td
                                                        className={`status-cell status-${leave.leave_status?.toLowerCase()}`}
                                                    >
                                                        {leave.leave_status || "Pending"}
                                                    </td>

                                                    <td style={{ textAlign: "center" }}>
                                                        {leave.leave_status?.toLowerCase() === "pending" ? (
                                                            <>
                                                                <button
                                                                    className="approve-btn"
                                                                    onClick={() => handleApprove(leave.leave_requests_id)}
                                                                >
                                                                    Approve
                                                                </button>
                                                                <button
                                                                    className="reject-btn"
                                                                    onClick={() => handleReject(leave.leave_requests_id)}
                                                                >
                                                                    Reject
                                                                </button>
                                                            </>
                                                        ) : (
                                                            "-"
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    ) : (
                                        <tr>
                                            <td colSpan="11" style={{ textAlign: "center" }}>
                                                No leave requests found
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* ✅ Reject Modal */}
            {showRejectModal && (
                <div className="modal-overlay">
                    <div className="modal-box">
                        <h3>Reason for Rejection</h3>
                        <textarea
                            value={rejectReason}
                            onChange={(e) => setRejectReason(e.target.value)}
                            placeholder="Enter reason here..."
                            rows="4"
                        />
                        <div className="modal-buttons">
                            <button className="reject-btn" onClick={confirmReject}>
                                Reject
                            </button>
                            <button className="cancel-btn" onClick={() => setShowRejectModal(false)}>
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ✅ Approve Review Panel - the paid/unpaid decision is made here */}
            {showApproveModal && (
                <div className="modal-overlay">
                    <div className="modal-box appr-modal">
                        <div className="appr-modal-head">
                            <h3>Approve leave</h3>
                            <button className="appr-x" onClick={() => setShowApproveModal(false)}><FaTimes /></button>
                        </div>

                        {approveLoading || !approveCtx ? (
                            <div className="appr-loading">Loading request…</div>
                        ) : (() => {
                            const r = approveCtx.request;
                            const b = approveCtx.balance;
                            const split = approveCtx.auto_split;
                            const dLabel = (n) => `${n} ${Number(n) === 1 ? "day" : "days"}`;
                            return (
                                <>
                                    {/* Request summary */}
                                    <div className="appr-req">
                                        <div className="appr-req-name">{r.emp_name} <span className="appr-req-id">({r.emp_id})</span></div>
                                        <div className="appr-req-meta">
                                            <span className="appr-chip">{r.leave_type}{r.type_is_paid ? "" : " · Unpaid type"}</span>
                                            {/* Breakdown is ALWAYS shown, including "Full" */}
                                            <span className="appr-when">
                                                {approveCtx.request.start_date} ({approveCtx.request.start_date_breakdown || "Full"})
                                                {approveCtx.request.end_date !== approveCtx.request.start_date &&
                                                    ` → ${approveCtx.request.end_date} (${approveCtx.request.end_date_breakdown || "Full"})`}
                                            </span>
                                            <span className="appr-req-days">{dLabel(r.requested_days)}</span>
                                        </div>
                                        {r.leave_description ? <div className="appr-req-desc">“{r.leave_description}”</div> : null}
                                    </div>

                                    {/* Balance tiles */}
                                    <div className="appr-tiles">
                                        <div className="appr-tile"><span className="appr-tile-val">{b.entitled_days}</span><span className="appr-tile-lbl">Max leaves</span></div>
                                        <div className="appr-tile"><span className="appr-tile-val">{b.used_days}</span><span className="appr-tile-lbl">Used this year</span></div>
                                        <div className={`appr-tile ${b.remaining_days <= 0 ? "appr-tile--red" : "appr-tile--green"}`}><span className="appr-tile-val">{b.remaining_days}</span><span className="appr-tile-lbl">Balance left ({b.year})</span></div>
                                    </div>

                                    {/* What "Automatic" would decide */}
                                    {split.unpaid_days > 0 && (
                                        <div className="appr-autosplit">
                                            ⚠ On <strong>Automatic</strong>, {dLabel(split.paid_days)} would be paid and <strong>{dLabel(split.unpaid_days)} unpaid (Loss of Pay)</strong> - they don't have enough balance for the whole request.
                                        </div>
                                    )}

                                    {/* Pay decision */}
                                    <div className="appr-pay-title">Pay decision</div>
                                    <div className="appr-pay-opts">
                                        {[
                                            ["Auto", "Automatic (normal)", "Pay the days they still have balance for. Anything extra is unpaid."],
                                            ["Paid", "Pay for all days", "Pay every day in full, even if they have no balance left."],
                                            ["Unpaid", "Don't pay for these days", "Every day is unpaid (Loss of Pay). Their balance is not used up."],
                                        ].map(([value, label, hint]) => (
                                            <label key={value} className={`appr-pay-opt${payChoice === value ? " selected" : ""}`}>
                                                <input type="radio" name="payChoice" value={value}
                                                    checked={payChoice === value} onChange={() => setPayChoice(value)} />
                                                <span className="appr-pay-text">
                                                    <span className="appr-pay-label">{label}</span>
                                                    <span className="appr-pay-hint">{hint}</span>
                                                </span>
                                            </label>
                                        ))}
                                    </div>

                                    {/* Recent history */}
                                    {approveCtx.history?.length > 0 && (
                                        <details className="appr-hist">
                                            <summary>Recent leave history ({approveCtx.history.length})</summary>
                                            <table className="appr-hist-table">
                                                <thead><tr><th>Type</th><th>From</th><th>To</th><th>Status</th><th>Pay</th></tr></thead>
                                                <tbody>
                                                    {approveCtx.history.map((h) => (
                                                        <tr key={h.leave_requests_id}>
                                                            <td>{h.leave_type}</td><td>{h.start_date}</td><td>{h.end_date}</td>
                                                            <td>{h.leave_status}</td><td>{h.pay_override || "Auto"}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </details>
                                    )}

                                    <div className="modal-buttons">
                                        <button className="approve-btn" onClick={confirmApprove}>Approve</button>
                                        <button className="cancel-btn" onClick={() => setShowApproveModal(false)}>Cancel</button>
                                    </div>
                                </>
                            );
                        })()}
                    </div>
                </div>
            )}
        </div>
    );
};

export default Leave_approvals;
